import * as Sentry from '@sentry/nextjs'
import { usePathname } from 'next/navigation'
import { useDispatch } from 'react-redux'

import { FbEventName, sendFbEventByName } from '@/legacy-src/analytics/fbPixelData'
import { addGtmEvent } from '@/legacy-src/analytics/gtm'
import toaster from '@/legacy-src/utilities/toaster'

import { useApis } from '../contexts/api-context'
import GeneralActions from '../redux/GeneralRedux'
import UserActions from '../redux/UserRedux'
import { type TherapistType } from '../types/TherapistStateType'
import { type Tokens, type UserType } from '../types/UserStateType'
import { setTokens } from '../utilities/setTokens'

type Data = Tokens & (UserType | TherapistType) & { emailInOtherCountry: string }
type IncomingParams = {
  readonly response: {
    readonly data: Data
    readonly status: number
  }
  readonly email?: string
  readonly redirectUrl?: string
  readonly therapistId?: number
  readonly diagnosis?: string | boolean
  readonly sendEvent?: boolean
}

/*
 * Use this hook when moving functionality from redux to functional components
 * */
const useProcessSuccessfulLogin = () => {
  const dispatch = useDispatch()
  const apis = useApis()
  const pathname = usePathname()

  return ({
    response,
    redirectUrl,
    email,
    diagnosis = false,
    therapistId = null,
    sendEvent = true,
  }: IncomingParams) => {
    const data = response.data
    if (email) {
      Sentry.setUser({ email })
    }

    const isRegistration = response.status === 201
    if (isRegistration) {
      addGtmEvent('registrationFinished', diagnosis ? 'diagnoseView' : 'dashboardView', { email })
      sendFbEventByName({ eventName: FbEventName.CompleteRegistration, user: { email } })
      toaster.success('component.toaster.registrationSuccess')
    }

    setTokens({
      api: apis.api,
      data: {
        refreshToken: data.refreshToken,
        accessToken: data.accessToken,
      },
    })

    // call necessary actions in reducer for correct behavior
    dispatch(GeneralActions.onChangeGeneralValue('emailIsInOtherCountry', !!data.emailInOtherCountry))
    dispatch(
      UserActions.getUserDataRequest(
        therapistId,
        !!diagnosis,
        sendEvent
          ? {
              event: 'loginSuccessful',
            }
          : {},
        undefined,
        redirectUrl,
        pathname,
      ),
    )
  }
}

export default useProcessSuccessfulLogin
